import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, LogOut, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import LeadsTable from "./LeadsTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadsDashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [summaryBusy, setSummaryBusy] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios
      .get(`${API}/admin/leads`, { headers })
      .then(({ data }) => setLeads(data.leads))
      .catch((e) => e.response?.status === 401 && onLogout())
      .finally(() => setLoading(false));
  }, [headers, onLogout]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return leads;
    return leads.filter((l) => ["name", "email", "phone", "instagram", "referrer"].some((k) => (l[k] || "").toLowerCase().includes(s)));
  }, [leads, q]);

  const updateLead = useCallback(async (id, changes) => {
    let previous;
    setLeads((prev) => prev.map((l) => (l.id === id ? ((previous = l), { ...l, ...changes }) : l)));
    try {
      const { data } = await axios.patch(`${API}/admin/leads/${id}`, changes, { headers });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
    } catch (e) {
      setLeads((prev) => prev.map((l) => (l.id === id ? previous : l)));
      if (e.response?.status === 401) onLogout();
      else toast.error("Couldn't save. Please try again.");
    }
  }, [headers, onLogout]);

  const sendSummary = async () => {
    setSummaryBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/summary/send`, {}, { headers });
      toast.success(`Summary sent — ${data.count} new sign-up${data.count === 1 ? "" : "s"} in the last 24h.`);
    } catch {
      toast.error("Couldn't send the summary email.");
    } finally {
      setSummaryBusy(false);
    }
  };

  const contactedCount = leads.filter((l) => l.contacted).length;

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/leads/export`, { headers, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: `back-to-you-leads-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="leads-dashboard" className="max-w-[1700px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Link to="/" className="flex items-center gap-2.5 font-display font-extrabold text-lg tracking-tight text-[#0B0B12]"><img src="/images/logo.png" alt="HERcules Health logo" className="h-9 w-auto" /><span>HER<span className="text-[#1B2FD8]">cules</span> Health</span></Link>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#0B0B12] mt-3 leading-none">REGISTRATIONS</h1>
          <p data-testid="leads-count" className="mt-3 text-sm text-[#4A4A66]">
            <span className="font-display font-extrabold text-2xl text-[#1B2FD8]">{leads.length}</span> women have said I'M IN
            <span className="mx-2 text-[#9A97B5]">·</span>
            <span data-testid="leads-contacted-count" className="font-semibold text-[#4B2A9E]">{contactedCount} contacted</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 rounded-full bg-white border border-[#E3E3F0] px-4 py-2.5 focus-within:border-[#1B2FD8] transition-colors">
            <Search size={16} className="text-[#9A97B5]" />
            <input data-testid="leads-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, IG…" className="bg-transparent outline-none text-sm w-44 sm:w-56 placeholder-[#9A97B5]" />
          </label>
          <button data-testid="send-summary-button" onClick={sendSummary} disabled={summaryBusy} className="flex items-center gap-2 rounded-full border border-[#0B0B12]/20 text-[#0B0B12] px-5 py-2.5 text-sm font-display font-bold hover:bg-[#0B0B12] hover:text-[#FFFFFF] disabled:opacity-60 transition-colors duration-300">
            <Mail size={16} /> {summaryBusy ? "Sending…" : "Email today's summary"}
          </button>
          <button data-testid="leads-export-button" onClick={exportCsv} className="flex items-center gap-2 rounded-full bg-[#1B2FD8] text-[#FFFFFF] px-5 py-2.5 text-sm font-display font-bold hover:bg-[#0B0B12] transition-colors duration-300">
            <Download size={16} /> Export CSV
          </button>
          <button data-testid="admin-logout-button" onClick={onLogout} className="flex items-center gap-2 rounded-full border border-[#0B0B12]/20 text-[#0B0B12] px-5 py-2.5 text-sm font-display font-bold hover:bg-[#0B0B12] hover:text-[#FFFFFF] transition-colors duration-300">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </header>
      <div className="mt-10">
        {loading ? <p className="text-[#4A4A66]">Loading registrations…</p> : <LeadsTable leads={filtered} onUpdate={updateLead} />}
      </div>
    </div>
  );
}
